/**
 * negative-scanner / frames.js — where the pictures are on the film, and how
 * to cut them out. Pure functions (tests/js/negative-scanner-frames.test.mjs).
 *
 * WHAT IS AROUND A FRAME
 * ──────────────────────
 * Photographed on a light, a strip of negative shows three kinds of area:
 *   - bare backlight (past the edge of the film, through the sprocket holes);
 *   - bare film base — the rebate along the edges and the gaps between frames,
 *     never exposed, so the THINNEST film there is (orange on colour film);
 *   - the pictures, which are denser than the base everywhere they were
 *     exposed.
 * A slide in a mount is the mirror case: the mount and unexposed slide film
 * are black, and the picture is the window of light inside.
 *
 * So the pictures are the "not border" pixels, and a frame edge is a long
 * straight line between the two. Instead of hunting for four corners (the
 * document scanner's problem — one page, perspective, clutter), the film is
 * de-rotated once and read as PROFILES: along the strip, the share of picture
 * pixels drops to almost nothing in every gap between frames and jumps back at
 * the next frame. Runs of high share are frames. The same pass finds one frame
 * in a phone photo, six in a strip and a grid of them in a flatbed scan.
 */

import { dec, enc } from './convert.js';

const DEG = Math.PI / 180;

function boxBlur(src, w, h, r) {
    const tmp = new Float32Array(w * h), out = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
        let acc = 0, cnt = 0;
        const row = y * w;
        for (let x = -r; x < w + r; x++) {
            if (x + r < w && x + r >= 0) { acc += src[row + x + r]; cnt++; }
            if (x - r - 1 >= 0 && x - r - 1 < w) { acc -= src[row + x - r - 1]; cnt--; }
            if (x >= 0 && x < w) tmp[row + x] = acc / cnt;
        }
    }
    for (let x = 0; x < w; x++) {
        let acc = 0, cnt = 0;
        for (let y = -r; y < h + r; y++) {
            if (y + r < h && y + r >= 0) { acc += tmp[(y + r) * w + x]; cnt++; }
            if (y - r - 1 >= 0 && y - r - 1 < h) { acc -= tmp[(y - r - 1) * w + x]; cnt--; }
            if (y >= 0 && y < h) out[y * w + x] = acc / cnt;
        }
    }
    return out;
}

/**
 * Which pixels are NOT picture: backlight + base on a negative, black mount +
 * backlight on a slide. Returns {border: Uint8Array, baseLum, lum}.
 */
export function borderMask(data, w, h, mode) {
    const N = w * h;
    const raw = new Float32Array(N);
    for (let i = 0, j = 0; i < N; i++, j += 4) raw[i] = (data[j] + 2 * data[j + 1] + data[j + 2]) / 4;
    const lum = boxBlur(raw, w, h, 1);
    const hist = new Uint32Array(256);
    for (let i = 0; i < N; i++) hist[Math.max(0, Math.min(255, Math.round(lum[i])))]++;
    const border = new Uint8Array(N);

    if (mode === 'slide') {
        // The mount is black; a picture's own blacks are rarely that black
        // AND that large. Clipped light past the mount counts as border too.
        // Bright light past the film is NOT marked: a slide's own whites are
        // just as clipped, and marking them cut the white side off a frame.
        // A rectangle that turns out to be bare light is dropped afterwards.
        // The mount is the DARKEST substantial peak and it is flat; a slide's
        // own maximum density still passes a little light, so the threshold
        // hugs the mount rather than sitting at a fixed "dark enough".
        let mount = -1;
        for (let i = 0; i < 80; i++) {
            let mass = 0;
            for (let k = -3; k <= 3; k++) { const j = i + k; if (j >= 0) mass += hist[j]; }
            if (mass >= 0.05 * N && hist[i] >= (hist[i - 1] || 0) && hist[i] >= hist[i + 1]) { mount = i; break; }
        }
        const t = mount >= 0 ? Math.min(40, mount * 1.4 + 6) : 12;
        for (let i = 0; i < N; i++) border[i] = lum[i] <= t ? 1 : 0;
        return { border, baseLum: null, lum };
    }

    // Negative. Clipped light (the lamp past the film, sprocket holes) is
    // border by definition and is left out of the search. Of what remains,
    // the base is the BRIGHTEST substantial peak: nothing on the film is
    // thinner than bare base, and the base is flat (a narrow peak), while a
    // picture's thin shadows spread over many levels.
    //
    // An Otsu split was the first idea and it failed on a strip photographed
    // with a lot of lamp around it: the split fell between lamp and base, and
    // the base went down with the pictures (measured on the 4-frame set).
    //
    // Pixels NEXT to clipped light are left out of the search too: every edge
    // of the lamp against the base (the strip's outline, each sprocket hole)
    // is a band of blends between the two, and a band of straight edges piles
    // into a few levels that look exactly like a flat base (measured on the
    // 4-frame set: a blend peak at 240, the base at 211).
    const nearClip = new Uint8Array(N);
    const clipR = Math.max(2, Math.round(Math.min(w, h) / 90));
    {
        const rowMax = new Uint8Array(N);
        for (let y = 0; y < h; y++) {
            let last = -1e9;
            for (let x = 0; x < w; x++) { const i = y * w + x; if (lum[i] >= 250) last = x; rowMax[i] = x - last <= clipR ? 1 : 0; }
            last = 1e9;
            for (let x = w - 1; x >= 0; x--) { const i = y * w + x; if (lum[i] >= 250) last = x; if (last - x <= clipR) rowMax[i] = 1; }
        }
        for (let x = 0; x < w; x++) {
            // vertical pass over the row-dilated mask
            let last = -1e9;
            for (let y = 0; y < h; y++) { const i = y * w + x; if (rowMax[i]) last = y; nearClip[i] = y - last <= clipR ? 1 : 0; }
            last = 1e9;
            for (let y = h - 1; y >= 0; y--) { const i = y * w + x; if (rowMax[i]) last = y; if (last - y <= clipR) nearClip[i] = 1; }
        }
    }
    const cnt = new Float64Array(256);
    let rest = 0;
    for (let i = 0; i < N; i++) {
        if (nearClip[i]) continue;
        const v = Math.round(lum[i]);
        if (v < 250) { cnt[v]++; rest++; }
    }
    if (rest < N * 0.05) for (let i = 0; i < 250; i++) { cnt[i] = hist[i]; rest += hist[i]; }
    const sm = new Float64Array(256);
    for (let i = 0; i < 250; i++) {
        let a = 0;
        for (let k = -3; k <= 3; k++) { const j = i + k; if (j >= 0 && j < 250) a += cnt[j] * (4 - Math.abs(k)); }
        sm[i] = a / 16;
    }
    // A peak counts only if it is NARROW: a flat base piles into a few
    // levels, while the blend of base and lamp along every edge of a
    // downscaled strip spreads thinly across the whole gap between them.
    // Measured on the fake-camera run: that blend reached 4 % of the frame at
    // lum 239 and was taken for the base (true base 210), and the rebates went
    // into the "picture".
    const massIn = (i, r) => { let m = 0; for (let k = -r; k <= r; k++) { const j = i + k; if (j >= 0 && j < 250) m += cnt[j]; } return m; };
    let peak = -1;
    for (let i = 248; i >= 1; i--) {
        if (sm[i] > 0 && sm[i] >= sm[i - 1] && sm[i] >= sm[i + 1]) {
            const m4 = massIn(i, 4);
            if (m4 >= 0.03 * rest && m4 >= 0.5 * massIn(i, 12)) { peak = i; break; }
        }
    }
    if (peak < 0) {
        // No flat base anywhere: everything below the lamp is picture.
        peak = 249;
    }
    // A picture's thinnest shadows are ~0.1 density above the base; the
    // threshold sits at 0.06, below the base by a factor 10^-0.06.
    const thr = enc(dec(peak) * Math.pow(10, -0.06)) * 255;
    for (let i = 0; i < N; i++) border[i] = lum[i] >= thr ? 1 : 0;
    return { border, baseLum: peak, lum, thr };
}

/**
 * Rotation of the film in the photo, radians, within ±maxDeg: the angle at
 * which the picture/border mask projects most sharply onto both axes.
 *
 * Measured, not assumed: a gradient-orientation histogram (the usual trick)
 * read 0.26° for a strip tilted 3° — a binary edge at a small angle is a
 * staircase of long horizontal runs, and every run votes for 0°. Projection
 * profiles do not care about the staircase: at the right angle each frame edge
 * falls into one or two bins and the profile jumps; at a wrong one it ramps.
 */
export function edgeAngle(border, w, h, maxDeg = 20) {
    const N = w * h;
    const step = N > 60000 ? 2 : 1;
    const pts = [];
    // Subsampled points are projected into bins as wide as the step — a
    // 1-px bin under a 2-px grid aliases into a comb at exactly 0° and wins.
    for (let y = 0; y < h; y += step) for (let x = 0; x < w; x += step) if (!border[y * w + x]) pts.push((x + 0.5 - w / 2) / step, (y + 0.5 - h / 2) / step);
    if (pts.length < 50) return 0;
    const R = Math.ceil(Math.hypot(w, h) / 2 / step) + 2;
    const pu = new Float64Array(2 * R + 1), pv = new Float64Array(2 * R + 1);
    const sharp = (a) => {
        const cs = Math.cos(a), sn = Math.sin(a);
        pu.fill(0); pv.fill(0);
        for (let i = 0; i < pts.length; i += 2) {
            const x = pts[i], y = pts[i + 1];
            pu[(x * cs + y * sn + R) | 0]++;
            pv[(-x * sn + y * cs + R) | 0]++;
        }
        let s = 0;
        for (let i = 1; i < pu.length; i++) { const d = pu[i] - pu[i - 1], e = pv[i] - pv[i - 1]; s += d * d + e * e; }
        return s;
    };
    let best = 0, bs = -1;
    for (let d = -maxDeg; d <= maxDeg; d += 1) { const v = sharp(d * DEG); if (v > bs) { bs = v; best = d; } }
    let lo = best - 1, hi = best + 1;
    for (let d = lo; d <= hi + 1e-9; d += 0.1) { const v = sharp(d * DEG); if (v > bs) { bs = v; best = d; } }
    return best * DEG;
}

/** Runs of "picture" along a profile, with hysteresis: a run starts where the
 *  share climbs past `on`, ends where it falls below `off`; the edges are then
 *  put where the share crosses one half. All three are RELATIVE to the
 *  profile's own top: across a single frame lying in a wide field of
 *  backlight, even the rows through the picture are only half picture, and an
 *  absolute threshold found nothing there. */
export function findRuns(frac, minLen, on = 0.55, off = 0.25) {
    const runs = [];
    const n = frac.length;
    const sorted = Array.from(frac).sort((a, b) => a - b);
    const top = sorted[Math.max(0, Math.floor(n * 0.98) - 1)] || 0;
    if (top <= 0.05) return runs;
    const ON = on * top, OFF = off * top, MID = 0.5 * top;
    let i = 0;
    while (i < n) {
        if (frac[i] < ON) { i++; continue; }
        let s = i;
        while (s > 0 && frac[s - 1] >= MID) s--;
        let e = i;
        while (e + 1 < n && frac[e + 1] >= OFF) e++;
        let ee = e;
        while (ee > s && frac[ee] < MID) ee--;
        runs.push([s, ee]);
        i = e + 1;
    }
    // Merge runs split by a hairline (a very dark picture detail that happens
    // to span the whole height is thinner than any real gap between frames).
    const merged = [];
    const hair = Math.max(1, Math.round(minLen * 0.12));
    for (const r of runs) {
        const last = merged[merged.length - 1];
        if (last && r[0] - last[1] - 1 <= hair) last[1] = r[1]; else merged.push(r.slice());
    }
    return merged.filter((r) => r[1] - r[0] + 1 >= minLen);
}

/**
 * Find the frames. `data` is an RGBA analysis image (a few hundred px wide).
 * Returns {angle, frames:[quad], base:[r,g,b]|null, border:bool}, quads in
 * the analysis image's pixel coordinates, ordered along the film.
 */
export function detectFrames(data, w, h, mode, opts = {}) {
    const { border, baseLum, lum } = borderMask(data, w, h, mode);
    const N = w * h;
    let bcount = 0;
    for (let i = 0; i < N; i++) bcount += border[i];
    const bfrac = bcount / N;
    const out = { angle: 0, frames: [], base: null, border: false };
    if (bfrac < 0.02 || bfrac > 0.97) return out;   // no border at all / no picture

    // Film base colour: pixels of the border near the base peak, not clipped.
    if (mode !== 'slide' && baseLum != null) {
        const vr = [], vg = [], vb = [];
        const lum = (j) => (data[j] + 2 * data[j + 1] + data[j + 2]) / 4;
        for (let i = 0, j = 0; i < N; i += 3, j += 12) {
            if (!border[i]) continue;
            const r = data[j], g = data[j + 1], b = data[j + 2];
            if (r >= 252 || g >= 252 || b >= 252) continue;
            if (Math.abs(lum(j) - baseLum) > 8) continue;
            vr.push(r); vg.push(g); vb.push(b);
        }
        if (vr.length > 20) {
            const med = (a) => { a.sort((p, q) => p - q); return a[a.length >> 1]; };
            out.base = [med(vr), med(vg), med(vb)];
        }
    }

    const ang = opts.angle != null ? opts.angle : edgeAngle(border, w, h);
    if (Math.abs(ang) > 30 * DEG) return out;
    out.angle = ang;
    const cs = Math.cos(ang), sn = Math.sin(ang);
    const cx = w / 2, cy = h / 2;
    // rotated extents
    let umin = Infinity, umax = -Infinity, vmin = Infinity, vmax = -Infinity;
    for (const [x, y] of [[0, 0], [w, 0], [w, h], [0, h]]) {
        const dx = x - cx, dy = y - cy;
        const u = dx * cs + dy * sn, v = -dx * sn + dy * cs;
        umin = Math.min(umin, u); umax = Math.max(umax, u); vmin = Math.min(vmin, v); vmax = Math.max(vmax, v);
    }
    const U = Math.ceil(umax - umin) + 1, V = Math.ceil(vmax - vmin) + 1;
    const ui = new Int32Array(N), vi = new Int32Array(N);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const dx = x + 0.5 - cx, dy = y + 0.5 - cy;
            const i = y * w + x;
            ui[i] = Math.floor(dx * cs + dy * sn - umin);
            vi[i] = Math.floor(-dx * sn + dy * cs - vmin);
        }
    }
    const profile = (axisU, lo, hi) => {
        // share of picture pixels per bin along one axis, restricted to a band
        // [lo, hi] of the other axis
        const L = axisU ? U : V;
        const pic = new Float64Array(L), all = new Float64Array(L);
        for (let i = 0; i < N; i++) {
            const a = axisU ? ui[i] : vi[i];
            const o = axisU ? vi[i] : ui[i];
            if (o < lo || o > hi) continue;
            all[a]++; if (!border[i]) pic[a]++;
        }
        let mx = 0;
        for (let k = 0; k < L; k++) mx = Math.max(mx, all[k]);
        const frac = new Float64Array(L);
        let prev = 0;
        for (let k = 0; k < L; k++) {
            // bins at the tilted image's corners hold a handful of pixels;
            // they carry the previous share instead of noise
            frac[k] = all[k] >= Math.max(3, mx * 0.08) ? pic[k] / all[k] : prev;
            prev = frac[k];
        }
        return { frac, all, pic };
    };
    const score = (rects) => {
        let s = 0;
        for (const r of rects) {
            for (let i = 0; i < N; i++) {
                if (ui[i] < r.u0 || ui[i] > r.u1 || vi[i] < r.v0 || vi[i] > r.v1) continue;
                s += border[i] ? -1.5 : 1;
            }
        }
        return s;
    };
    const decompose = (firstU) => {
        const L1 = firstU ? U : V, L2 = firstU ? V : U;
        const p1 = profile(firstU, -1, 1e9);
        const runs1 = findRuns(p1.frac, Math.max(4, Math.round(L1 * 0.06)));
        const rects = [];
        for (const [a0, a1] of runs1) {
            const p2 = profile(!firstU, a0, a1);
            const runs2 = findRuns(p2.frac, Math.max(4, Math.round(L2 * 0.06)));
            for (const [b0, b1] of runs2) {
                const r = firstU ? { u0: a0, u1: a1, v0: b0, v1: b1 } : { u0: b0, u1: b1, v0: a0, v1: a1 };
                rects.push(r);
            }
        }
        // Within its own extent, re-tighten each frame on BOTH axes: a frame
        // of a strip inherits the strip's height from the first pass.
        return rects.map((r) => {
            const pu = profile(true, r.v0, r.v1);
            const ru = findRuns(pu.frac.subarray(r.u0, r.u1 + 1), Math.max(3, Math.round((r.u1 - r.u0) * 0.5)));
            const pv = profile(false, r.u0, r.u1);
            const rv = findRuns(pv.frac.subarray(r.v0, r.v1 + 1), Math.max(3, Math.round((r.v1 - r.v0) * 0.5)));
            const o = Object.assign({}, r);
            if (ru.length) { o.u0 = r.u0 + ru[0][0]; o.u1 = r.u0 + ru[ru.length - 1][1]; }
            if (rv.length) { o.v0 = r.v0 + rv[0][0]; o.v1 = r.v0 + rv[rv.length - 1][1]; }
            return o;
        });
    };
    const A = decompose(false), B = decompose(true);
    let rects = score(A) >= score(B) ? A : B;
    // Drop crumbs (a real frame is at least 1.5 % of the photo) and bare
    // light: a rectangle that is nearly all clipped white with no texture is
    // the lamp seen past the film, not a picture.
    rects = rects.filter((r) => {
        if ((r.u1 - r.u0 + 1) * (r.v1 - r.v0 + 1) < N * 0.015) return false;
        let n = 0, hot = 0;
        for (let i = 0; i < N; i += 2) {
            if (ui[i] < r.u0 || ui[i] > r.u1 || vi[i] < r.v0 || vi[i] > r.v1) continue;
            n++; if (lum[i] >= 250) hot++;
        }
        return !n || hot / n < 0.9;
    });
    if (!rects.length) return out;
    // Reading order along the film: the long axis of the arrangement.
    rects.sort((a, b) => (Math.abs(a.v0 - b.v0) > (a.v1 - a.v0) / 2 ? a.v0 - b.v0 : a.u0 - b.u0));

    const toXY = (u, v) => {
        const uu = u + umin, vv = v + vmin;
        return { x: cx + uu * cs - vv * sn, y: cy + uu * sn + vv * cs };
    };
    out.frames = rects.map((r) => {
        // Pull in by one analysis pixel plus 0.5 %: the boundary itself is a
        // soft blend of base and picture, and a hairline of orange along the
        // edge of a saved frame looks like a mistake.
        const iu = 1 + (r.u1 - r.u0) * 0.005, iv = 1 + (r.v1 - r.v0) * 0.005;
        const u0 = r.u0 + iu, u1 = r.u1 + 1 - iu, v0 = r.v0 + iv, v1 = r.v1 + 1 - iv;
        return [toXY(u0, v0), toXY(u1, v0), toXY(u1, v1), toXY(u0, v1)];
    });
    out.border = true;
    return out;
}

/** Quad covering a whole w×h image. */
export function fullQuad(w, h) {
    return [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
}

export function scaleQuad(q, sx, sy = sx) {
    return q.map((p) => ({ x: p.x * sx, y: p.y * sy }));
}

/** The frame nearest the middle of the view — the one the person is pointing at. */
export function centralFrame(frames, w, h) {
    let best = null, bd = Infinity;
    for (const q of frames) {
        const cx = (q[0].x + q[1].x + q[2].x + q[3].x) / 4, cy = (q[0].y + q[1].y + q[2].y + q[3].y) / 4;
        const d = Math.hypot(cx - w / 2, cy - h / 2);
        if (d < bd) { bd = d; best = q; }
    }
    return best;
}

/** Natural size of a quad: mean of opposite sides. */
export function quadSize(q) {
    const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    return { w: (d(q[0], q[1]) + d(q[3], q[2])) / 2, h: (d(q[0], q[3]) + d(q[1], q[2])) / 2 };
}

/** Homography mapping the unit square (0,0)(1,0)(1,1)(0,1) onto quad q
 *  (Heckbert's square-to-quad). Returns [a..h] with i = 1. */
export function squareToQuad(q) {
    const [p0, p1, p2, p3] = q;
    const dx1 = p1.x - p2.x, dx2 = p3.x - p2.x, dy1 = p1.y - p2.y, dy2 = p3.y - p2.y;
    const sx = p0.x - p1.x + p2.x - p3.x, sy = p0.y - p1.y + p2.y - p3.y;
    let g = 0, hh = 0;
    if (Math.abs(sx) > 1e-9 || Math.abs(sy) > 1e-9) {
        const den = dx1 * dy2 - dx2 * dy1;
        g = (sx * dy2 - dx2 * sy) / den;
        hh = (dx1 * sy - sx * dy1) / den;
    }
    const a = p1.x - p0.x + g * p1.x, b = p3.x - p0.x + hh * p3.x, c = p0.x;
    const d = p1.y - p0.y + g * p1.y, e = p3.y - p0.y + hh * p3.y, f = p0.y;
    return [a, b, c, d, e, f, g, hh];
}

/** Output size for a quad with rotation applied. */
export function outSize(q, rot, maxLong = Infinity) {
    const s = quadSize(q);
    let W = Math.max(1, Math.round(s.w)), H = Math.max(1, Math.round(s.h));
    const k = Math.min(1, maxLong / Math.max(W, H));
    W = Math.max(1, Math.round(W * k)); H = Math.max(1, Math.round(H * k));
    return (rot % 180) ? { w: H, h: W, cw: W, ch: H } : { w: W, h: H, cw: W, ch: H };
}

/**
 * Cut the quad out of `src` (RGBA w×h), straighten it, rotate by `rot`
 * (0/90/180/270, clockwise) and mirror if `flip` — one resampling pass.
 * Bilinear. Returns {data, w, h}.
 */
export function warp(src, sw, sh, q, opts = {}) {
    const rot = ((opts.rot || 0) % 360 + 360) % 360;
    const flip = !!opts.flip;
    const size = outSize(q, rot, opts.maxLong || Infinity);
    const W = size.w, H = size.h, CW = size.cw, CH = size.ch;
    const out = new Uint8ClampedArray(W * H * 4);
    const [a, b, c, d, e, f, g, hh] = squareToQuad(q);
    for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
            let fx = x + 0.5, fy = y + 0.5;
            if (flip) fx = W - fx;
            let u, v;
            if (rot === 0) { u = fx; v = fy; }
            else if (rot === 90) { u = fy; v = CH - fx; }
            else if (rot === 180) { u = CW - fx; v = CH - fy; }
            else { u = CW - fy; v = fx; }
            const s = u / CW, t = v / CH;
            const den = g * s + hh * t + 1;
            let X = (a * s + b * t + c) / den - 0.5, Y = (d * s + e * t + f) / den - 0.5;
            if (X < 0) X = 0; else if (X > sw - 1) X = sw - 1;
            if (Y < 0) Y = 0; else if (Y > sh - 1) Y = sh - 1;
            const x0 = X | 0, y0 = Y | 0, x1 = x0 + 1 < sw ? x0 + 1 : x0, y1 = y0 + 1 < sh ? y0 + 1 : y0;
            const ax = X - x0, ay = Y - y0;
            const i00 = (y0 * sw + x0) * 4, i10 = (y0 * sw + x1) * 4, i01 = (y1 * sw + x0) * 4, i11 = (y1 * sw + x1) * 4;
            const o = (y * W + x) * 4;
            for (let k = 0; k < 3; k++) {
                const top = src[i00 + k] + (src[i10 + k] - src[i00 + k]) * ax;
                const bot = src[i01 + k] + (src[i11 + k] - src[i01 + k]) * ax;
                out[o + k] = top + (bot - top) * ay;
            }
            out[o + 3] = 255;
        }
    }
    return { data: out, w: W, h: H };
}

/**
 * Where a point of the OUTPUT (after warp/rotate/flip, normalised 0..1) sits
 * in the source — for a tap on the preview.
 */
export function outToSrc(q, rot, flip, nx, ny) {
    rot = ((rot || 0) % 360 + 360) % 360;
    let fx = flip ? 1 - nx : nx, fy = ny;
    let s, t;
    if (rot === 0) { s = fx; t = fy; }
    else if (rot === 90) { s = fy; t = 1 - fx; }
    else if (rot === 180) { s = 1 - fx; t = 1 - fy; }
    else { s = 1 - fy; t = fx; }
    const [a, b, c, d, e, f, g, hh] = squareToQuad(q);
    const den = g * s + hh * t + 1;
    return { x: (a * s + b * t + c) / den, y: (d * s + e * t + f) / den };
}

/** IoU of two convex quads, by rasterising both onto a grid (tests, tuning). */
export function quadIoU(a, b, w, h) {
    const { inQuadPt } = { inQuadPt: (q, x, y) => {
        let s = 0;
        for (let i = 0; i < 4; i++) {
            const p = q[i], n = q[(i + 1) & 3];
            const cr = (n.x - p.x) * (y - p.y) - (n.y - p.y) * (x - p.x);
            if (cr === 0) continue;
            const sg = cr > 0 ? 1 : -1;
            if (!s) s = sg; else if (sg !== s) return false;
        }
        return true;
    } };
    let inter = 0, uni = 0;
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const ia = inQuadPt(a, x + 0.5, y + 0.5), ib = inQuadPt(b, x + 0.5, y + 0.5);
            if (ia && ib) inter++;
            if (ia || ib) uni++;
        }
    }
    return uni ? inter / uni : 0;
}
